import { RefreshCw } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { PageHeader } from '@/shared/components/ui/PageHeader'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { Button } from '@/shared/components/ui/Button'
import { CHART_COLORS, CHART_DEFAULTS } from '@/shared/lib/chartTheme'
import { useDashboard, useTriggerSync } from '../hooks/useGFO'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell,
} from 'recharts'

/* eslint-disable @typescript-eslint/no-explicit-any */

const formatNumber = (n: number) => n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : n.toLocaleString('fr-FR')
const formatPercent = (n: number) => `${(n * 100).toFixed(2)}%`

function DetailRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-ink-border/20 last:border-0">
      <span className="text-[12px] text-ink-muted">{label}</span>
      <span className="text-[13px] font-semibold text-ink">{value}</span>
    </div>
  )
}

const barGradient = [CHART_COLORS.quaternary, '#8B73FF', '#9B85FF', '#AB97FF', '#BBa9FF']

export function DashboardPage() {
  const { data: dash, isLoading } = useDashboard()
  const sync = useTriggerSync()

  const d = dash as any
  const opportunities = d?.top_opportunities || []
  const chartData = opportunities.map((k: any) => ({
    name: (k.keyword as string).length > 20 ? (k.keyword as string).slice(0, 20) + '…' : k.keyword,
    score: k.opportunity_score,
    impressions: k.impressions,
  }))

  return (
    <div className="space-y-6">
      <PageHeader
        moduleId="gfo"
        moduleFullName="Google Footprint Optimizer"
        title="Dashboard"
        subtitle="Vue d'ensemble de votre empreinte Google"
        actions={
          <Button variant="gold" onClick={() => sync.mutate()} loading={sync.isPending}>
            <RefreshCw size={14} className={cn(sync.isPending && 'animate-spin')} /> Synchroniser
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Impressions" value={isLoading ? '…' : formatNumber(d?.total_impressions ?? 0)} accentColor="#7B61FF" loading={isLoading}
          detailTitle="Impressions — Détail"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Impressions totales" value={formatNumber(d?.total_impressions ?? 0)} />
              {opportunities.slice(0, 5).map((k: any) => (
                <DetailRow key={k.keyword} label={k.keyword} value={formatNumber(k.impressions ?? 0)} />
              ))}
            </div>
          } />
        <KpiCard label="Clics" value={isLoading ? '…' : formatNumber(d?.total_clicks ?? 0)} accentColor="#7B61FF" loading={isLoading}
          detailTitle="Clics organiques — Détail"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Clics totaux" value={formatNumber(d?.total_clicks ?? 0)} />
              <DetailRow label="Impressions" value={formatNumber(d?.total_impressions ?? 0)} />
              <DetailRow label="CTR moyen" value={formatPercent(d?.avg_ctr ?? 0)} />
              <div className="pt-2 mt-2 border-t border-ink-border/40">
                <p className="text-[11px] text-ink-faint">Ratio clics/impressions. Un CTR en hausse signifie des snippets plus attractifs.</p>
              </div>
            </div>
          } />
        <KpiCard label="CTR moyen" value={isLoading ? '…' : formatPercent(d?.avg_ctr ?? 0)} accentColor="#C9A84C" loading={isLoading}
          detailTitle="CTR — Analyse"
          detailNode={
            <div className="space-y-3">
              <DetailRow label="CTR global" value={formatPercent(d?.avg_ctr ?? 0)} />
              {[
                { range: '> 15%', label: 'Excellent — position 1 ou rich snippets', color: '#0F7173' },
                { range: '8% – 15%', label: 'Bon — positions 1-3', color: '#1E6B3A' },
                { range: '3% – 8%', label: 'Correct — positions 3-8', color: '#C9A84C' },
                { range: '< 3%', label: 'Faible — snippets à optimiser', color: '#E8562A' },
              ].map((r) => (
                <div key={r.range} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: r.color }} />
                  <span className="text-[12px] text-ink font-medium w-16">{r.range}</span>
                  <span className="text-[11px] text-ink-muted">{r.label}</span>
                </div>
              ))}
            </div>
          } />
        <KpiCard label="Note moyenne" value={isLoading ? '…' : (d?.avg_rating ?? 0).toFixed(1)} accentColor="#E8562A" loading={isLoading}
          detailTitle="Note Google Business"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Note moyenne" value={(d?.avg_rating ?? 0).toFixed(1) + ' ★'} />
              <DetailRow label="Avis total" value={formatNumber(d?.total_reviews ?? 0)} />
              <DetailRow label="Sans réponse" value={d?.reviews_needing_response ?? 0} />
              <div className="pt-2 mt-2 border-t border-ink-border/40">
                <p className="text-[11px] text-ink-faint">Objectif : maintenir au-dessus de 4.5★ pour maximiser les clics dans le Local Pack.</p>
              </div>
            </div>
          } />
        <KpiCard label="Avis total" value={isLoading ? '…' : formatNumber(d?.total_reviews ?? 0)} accentColor="#7B61FF" loading={isLoading}
          detailTitle="Avis Google"
          detailNode={
            <div className="space-y-1">
              <DetailRow label="Avis total" value={formatNumber(d?.total_reviews ?? 0)} />
              <DetailRow label="Note moyenne" value={(d?.avg_rating ?? 0).toFixed(1) + ' ★'} />
              <DetailRow label="En attente de réponse" value={d?.reviews_needing_response ?? 0} />
              <DetailRow label="Taux de réponse" value={d?.total_reviews > 0 ? `${(((d?.total_reviews - (d?.reviews_needing_response ?? 0)) / d?.total_reviews) * 100).toFixed(0)}%` : '—'} />
            </div>
          } />
        <KpiCard label="Sans réponse" value={isLoading ? '…' : formatNumber(d?.reviews_needing_response ?? 0)} accentColor="#E8562A" loading={isLoading}
          detailTitle="Avis sans réponse"
          detailNode={
            <div className="space-y-2">
              <DetailRow label="Avis sans réponse" value={d?.reviews_needing_response ?? 0} />
              <DetailRow label="Total des avis" value={formatNumber(d?.total_reviews ?? 0)} />
              <div className="pt-2 mt-2 border-t border-ink-border/40">
                <p className="text-[11px] text-ink-faint">Priorité : 1-2★ d'abord, puis les récents {'<'} 7 jours, puis les 5★ sans réponse.</p>
              </div>
            </div>
          } />
        <KpiCard label="Recommandations" value={isLoading ? '…' : formatNumber(d?.pending_recommendations ?? 0)} accentColor="#0F7173" loading={isLoading}
          detailTitle="Recommandations SEO"
          detailNode={
            <div className="space-y-2">
              <DetailRow label="Recommandations en attente" value={d?.pending_recommendations ?? 0} />
              {opportunities.length > 0 && (
                <>
                  <p className="text-[10px] font-medium tracking-wider uppercase text-ink-faint pt-2">Top opportunités</p>
                  {opportunities.slice(0, 5).map((k: any) => (
                    <div key={k.keyword} className="flex items-center justify-between py-1">
                      <span className="text-[12px] text-ink-muted truncate mr-2">{k.keyword}</span>
                      <span className="text-[12px] font-semibold text-ink flex-shrink-0">{(k.opportunity_score ?? 0).toFixed(1)}/10</span>
                    </div>
                  ))}
                </>
              )}
            </div>
          } />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-card border border-ink-border p-6 shadow-card">
          <h3 className="font-serif text-sm font-semibold text-ink mb-4">Top Opportunités</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical" barSize={18}>
                <CartesianGrid {...CHART_DEFAULTS.grid} horizontal={false} />
                <XAxis type="number" {...CHART_DEFAULTS.axis} />
                <YAxis type="category" dataKey="name" width={130} {...CHART_DEFAULTS.axis} tick={{ ...CHART_DEFAULTS.axis.tick, fontSize: 11 }} />
                <Tooltip {...CHART_DEFAULTS.tooltip} />
                <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                  {chartData.map((_: unknown, i: number) => <Cell key={i} fill={barGradient[i % barGradient.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState compact title="Aucune donnée" description="Lancez une synchronisation pour voir les opportunités." />
          )}
        </div>

        <div className="bg-white rounded-card border border-ink-border p-6 shadow-card">
          <h3 className="font-serif text-sm font-semibold text-ink mb-4">Mots-clés par impressions</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical" barSize={18}>
                <CartesianGrid {...CHART_DEFAULTS.grid} horizontal={false} />
                <XAxis type="number" {...CHART_DEFAULTS.axis} />
                <YAxis type="category" dataKey="name" width={130} {...CHART_DEFAULTS.axis} tick={{ ...CHART_DEFAULTS.axis.tick, fontSize: 11 }} />
                <Tooltip {...CHART_DEFAULTS.tooltip} />
                <Bar dataKey="impressions" radius={[0, 4, 4, 0]} fill={CHART_COLORS.quaternary} fillOpacity={0.6} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <EmptyState compact title="Aucune donnée" description="Les impressions apparaîtront après la synchronisation." />
          )}
        </div>
      </div>
    </div>
  )
}
